import { queryClient } from '@/plugins/vue-query';
import { defineStore } from 'pinia';
import { useAuthStore } from './auth.store';
import type { OrderSummary, OrderSummaryResponse } from '@/interfaces/orderSummary.interface';
import { OrderSubscriptionStatus } from '@/interfaces/orderSummary.interface';
import { cxmService } from '@/services/cxm.service';
import type {
  CreateOrderCancellationDTO,
  CreateOrderPlanChangeDTO,
} from '@/services/cxm.service.interface';
import * as Sentry from '@sentry/vue';
import { useNoticeStore } from './notice.store';

interface EcommerceState {
  orderSummary?: OrderSummary;
  isLoading: boolean;
}

function getCurrentOrderPlanPrice(orderPlan?: OrderSummary['current_order_plan']): number {
  return Number(orderPlan?.price ?? orderPlan?.original_plan_price);
}

function resolveOrderSummary(payload?: OrderSummaryResponse): OrderSummary | undefined {
  if (!payload) return;
  return 'summary' in payload ? payload.summary : payload;
}

export const useOrderSummaryStore = defineStore('order-summary', {
  state: (): EcommerceState => ({
    orderSummary: undefined,
    isLoading: false,
  }),
  getters: {
    hasActiveOrderPlanChange: (state) => {
      return !!state.orderSummary?.active_order_plan_change;
    },
    hasStalePlan: (state) => {
      // Business rule: stale plan warnings compare what the customer is charged today
      // against the current catalog price. original_plan_price is historical and may
      // remain stale after CXM syncs order_plan.price.
      const currentOrderPlanPrice = getCurrentOrderPlanPrice(
        state.orderSummary?.current_order_plan,
      );
      const currentCatalogPrice = Number(state.orderSummary?.current_order_plan?.plan.price);
      return (
        !(isNaN(currentOrderPlanPrice) || isNaN(currentCatalogPrice)) &&
        ((currentCatalogPrice === 0 && currentOrderPlanPrice > 0) ||
          (currentCatalogPrice > 0 && currentOrderPlanPrice <= currentCatalogPrice * 0.85))
      );
    },
    hasFreePlanActive: (state) => {
      return (
        Number(state.orderSummary?.current_order_plan?.plan?.id) ===
        Number(state.orderSummary?.default_plan?.id)
      );
    },
    hasFailedActiveOrderSubscription: (state) => {
      return (
        state.orderSummary?.active_order_subscription?.status === OrderSubscriptionStatus.FAILED
      );
    },
  },
  actions: {
    async init() {
      await this.getOrderSummary();
    },
    async getOrderSummary() {
      const authStore = useAuthStore();
      const noticesStore = useNoticeStore();

      if (!authStore.user?.current_order_id) return;

      try {
        this.isLoading = true;
        const response = await queryClient.ensureQueryData<OrderSummary | undefined>({
          queryKey: ['order-summary', authStore.user?.current_order_id],
          queryFn: async () => {
            if (!authStore.user?.current_order_id) return;
            const request = await cxmService.getOrderSummary(authStore.user?.current_order_id);
            const summary = resolveOrderSummary(request.data.data);
            queryClient.setQueryData(['order-summary', authStore.user?.current_order_id], summary);
            return summary;
          },
        });

        this.orderSummary = response;
        noticesStore.checkNotices();
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        this.isLoading = false;
      }
    },
    async createOrderPlanChange(order_id: number, data: CreateOrderPlanChangeDTO) {
      await cxmService.createOrderPlanChange(order_id, data);
      this.refresh();
    },
    async cancelOrderPlanChange(order_id: number, order_plan_change_id: number) {
      await cxmService.cancelOrderPlanChange(order_id, order_plan_change_id);
      this.refresh();
    },
    async cancelOrder(data: CreateOrderCancellationDTO) {
      const response = await cxmService.createOrderCancellation(data);
      const { status } = response.data.data;

      // CXM processes transitions during creation; reopening a closed cancellation fails.
      if (status !== 'CLOSED' && !(data.type === 'SCHEDULED' && status === 'OPENED')) {
        throw new Error('Order cancellation was not processed. Please retry.');
      }
    },
    async refresh() {
      const authStore = useAuthStore();
      await queryClient.removeQueries({
        queryKey: ['order-summary', authStore.user?.current_order_id],
      });
      await this.getOrderSummary();
    },
    async clear() {
      await queryClient.removeQueries({
        queryKey: ['order-summary'],
      });
      this.orderSummary = undefined;
    },
  },
});
